/**
 * filterSelect — Alpine.js component for a styleable custom dropdown.
 *
 * Wraps a hidden <select class="filter-select"> so that filtering.js
 * (which relies on .filter-select change events, FormData, and
 * selectedIndex resets) continues to work without modification.
 *
 * Usage in HTML: data-x-data="filterSelect"
 * Expects: a sibling <select class="filter-select"> inside the same wrapper element.
 */
export default function filterSelect() {
    return {
        open: false,
        label: '',
        selectedValue: '',
        _select: null,

        init() {
            this._select = this.$el.querySelector('select.filter-select');
            if (!this._select) return;

            // Placeholder label comes from data-label on the wrapper element (e.g. "Team Members").
            this._placeholder = this.$el.dataset.label || this._select.options[0]?.text || '';

            // Set initial display label: placeholder when nothing is selected, option text otherwise.
            const idx = this._select.selectedIndex;
            this.label = this._select.value ? this._select.options[idx]?.text || '' : this._placeholder;
            this.selectedValue = this._select.value || '';

            // When filtering.js calls form.reset() (clear button), sync display.
            const form = this.$el.closest('form');
            if (form) {
                form.addEventListener('reset', () => {
                    this.$nextTick(() => {
                        this.label = this._placeholder;
                        this.selectedValue = '';
                        this.open = false;
                    });
                });
            }
        },

        /**
         * Called when the user clicks an option.
         * Updates display state, syncs the hidden select, and fires a change
         * event so filtering.js triggers an AJAX fetch.
         *
         * @param {string} value - The option value (empty string = "All")
         * @param {string} label - The human-readable label to display
         */
        choose(value, label) {
            this.label = label;
            this.selectedValue = value;
            this.open = false;

            if (this._select) {
                this._select.value = value;
                this._select.dispatchEvent(new Event('change', { bubbles: true }));
            }
        },
    };
}
